/**
 * ValidationToast — surfaces the last ValidationResult error from the store.
 *
 * Originally derived from the (removed) board-game-playground for visual parity. UI is NOT shared (CLAUDE.md).
 * Auto-dismisses after 4s or when the parent clears lastError.
 *
 * Locale strings come from useLocale() (ko-KR primary, en-US parity).
 * spec §11.3 error codes — all 22 codes covered via the locale map.
 */

import type { ValidationResult } from "@cloudnest/cascadia-engine";
import { useEffect, useState } from "react";
import { useLocale } from "../i18n/useLocale";

export interface ValidationToastProps {
  readonly lastError: ValidationResult | null;
  readonly onDismiss: () => void;
}

const DISMISS_DELAY_MS = 4000;

export function ValidationToast({
  lastError,
  onDismiss,
}: ValidationToastProps) {
  const { t: tKo } = useLocale("ko-KR");
  const { t: tEn } = useLocale("en-US");
  const [pinned, setPinned] = useState(false);

  useEffect(() => {
    if (!lastError) {
      setPinned(false);
      return;
    }
    if (pinned) return;
    const timer = setTimeout(() => onDismiss(), DISMISS_DELAY_MS);
    return () => clearTimeout(timer);
  }, [lastError, pinned, onDismiss]);

  if (!lastError) return null;

  const errorCode = lastError.error ?? "ERR_UNKNOWN_ACTION";

  return (
    <div
      data-testid="cascadia-validation-toast"
      aria-live="assertive"
      role="alert"
      onClick={() => setPinned((v) => !v)}
      style={{
        background: "var(--cn-cascadia-illegal-bg)",
        border: "1px solid var(--cn-cascadia-illegal)",
        borderRadius: 6,
        padding: "8px 16px",
        margin: "4px 12px",
        fontSize: 13,
        color: "var(--cn-cascadia-illegal)",
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <span>{tKo(errorCode)}</span>
      <span style={{ fontSize: 11, opacity: 0.8 }}>{tEn(errorCode)}</span>
      {pinned ? (
        <span style={{ fontSize: 10, opacity: 0.6, marginTop: 2 }}>
          ⓘ {tKo("btn_learn_more")} / Tap to dismiss
        </span>
      ) : null}
    </div>
  );
}
